import { Button, Spacer, VerticalCenter } from '@vapor/ui';
import Link from 'next/link';
import React, { useMemo, useRef } from 'react';
import { FadeIn } from '../components/animation/fadeIn';
import { FadeInUp } from '../components/animation/fadeInUp';
import { Center } from '../components/boxes/center';
import { Header, HeaderInner, HeaderMask } from '../components/boxes/header';
import { HeaderLogo } from '../components/boxes/headerLogo';
import { Logo } from '../components/logo';
import { BottomCTA } from '../components/marketing/bottomCta';
import { Card, CardGrid, CardGridWrapper } from '../components/marketing/card';
import { DemoFrame } from '../components/marketing/demoFrame';
import { Buttons } from '../components/marketing/buttons';
import { BigSubtitle } from '../components/text/bigSubtitle';
import { MainHeader } from '../components/text/mainHeader';
import { Mark } from '../components/text/mark';
import { Demo } from '../svgs/demo';
import { MobileIcon } from '../svgs/mobile';
import { TeamIcon } from '../svgs/team';
import { RepeatIcon } from '../svgs/repeat';
import { SubtaskIcon } from '../svgs/subtask';
import { useIsLoggedIn, useUser } from '../state/user';
import { MarketingLayout } from '../layout/marketing';
import { SyncIcon } from '../svgs/sync';
import { ShareIcon } from '../svgs/share';
import { BigCard } from '../components/marketing/bigCard';
import { ReallyBigText } from '../components/marketing/reallyBigText';
import { BigContent } from '../components/boxes/content';
import { BigTitle } from '../components/text/title';
import { Subtitle } from '../components/text/subtitle';
import { HighlightSection, HighlightSectionInner } from '../components/boxes/highlightSection';
import { Grid } from '../components/grid';
import { TodayIcon } from '../svgs/today';
import { MultiCalendarIcon } from '../svgs/multiCalendar';

export default function Home() {
  let { loggedIn } = useIsLoggedIn();
  let featuresStartRef = useRef() as React.RefObject<HTMLDivElement>;
  let { user, loading } = useUser(true);

  let demoUrl = useMemo(() => {
    let url = new URL('https://demo-calendar.weekrise.com/cal/demo');
    if (user?.photoUrl) url.searchParams.set('photoUrl', user?.photoUrl);

    return url.toString();
  }, [user?.photoUrl]);

  return (
    <MarketingLayout>
      <Header
        style={{
          backgroundImage:
            'url("data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwMCIgaGVpZ2h0PSIyMjE5IiB2aWV3Qm94PSIwIDAgMjAwMCAyMjE5IiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPgo8ZyBjbGlwLXBhdGg9InVybCgjY2xpcDApIj4KPHJlY3QgeT0iMC4wODc3Njg2IiB3aWR0aD0iMjAwMCIgaGVpZ2h0PSIyMjE5IiBmaWxsPSJ1cmwoI3BhaW50MF9saW5lYXIpIi8+CjxnIGZpbHRlcj0idXJsKCNmaWx0ZXIwX2YpIj4KPGNpcmNsZSBjeD0iMzIzLjU2NCIgY3k9IjI4NC41NjQiIHI9IjUzMy45NDMiIHRyYW5zZm9ybT0icm90YXRlKC0yMi44ODY0IDMyMy41NjQgMjg0LjU2NCkiIGZpbGw9InVybCgjcGFpbnQxX2xpbmVhcikiLz4KPC9nPgo8ZyBmaWx0ZXI9InVybCgjZmlsdGVyMV9mKSI+CjxjaXJjbGUgY3g9IjIxNi4yNDciIGN5PSIxMzIwLjMzIiByPSIyODcuMTcxIiB0cmFuc2Zvcm09InJvdGF0ZSgtMjIuODg2NCAyMTYuMjQ3IDEzMjAuMzMpIiBmaWxsPSIjRkZDNjU3IiBmaWxsLW9wYWNpdHk9IjAuNTMiLz4KPC9nPgo8ZyBmaWx0ZXI9InVybCgjZmlsdGVyMl9mKSI+CjxlbGxpcHNlIGN4PSIxNjU1LjY3IiBjeT0iMTExMC40NSIgcng9IjQ2MC41IiByeT0iNzMxIiB0cmFuc2Zvcm09InJvdGF0ZSg0OS4zODA4IDE2NTUuNjcgMTExMC40NSkiIGZpbGw9InVybCgjcGFpbnQyX2xpbmVhcikiLz4KPC9nPgo8L2c+CjxkZWZzPgo8ZmlsdGVyIGlkPSJmaWx0ZXIwX2YiIHg9Ii04NzYiIHk9Ii05MTUiIHdpZHRoPSIyMzk5LjEzIiBoZWlnaHQ9IjIzOTkuMTMiIGZpbHRlclVuaXRzPSJ1c2VyU3BhY2VPblVzZSIgY29sb3ItaW50ZXJwb2xhdGlvbi1maWx0ZXJzPSJzUkdCIj4KPGZlRmxvb2QgZmxvb2Qtb3BhY2l0eT0iMCIgcmVzdWx0PSJCYWNrZ3JvdW5kSW1hZ2VGaXgiLz4KPGZlQmxlbmQgbW9kZT0ibm9ybWFsIiBpbj0iU291cmNlR3JhcGhpYyIgaW4yPSJCYWNrZ3JvdW5kSW1hZ2VGaXgiIHJlc3VsdD0ic2hhcGUiLz4KPGZlR2F1c3NpYW5CbHVyIHN0ZERldmlhdGlvbj0iMjUwIiByZXN1bHQ9ImVmZmVjdDFfZm9yZWdyb3VuZEJsdXIiLz4KPC9maWx0ZXI+CjxmaWx0ZXIgaWQ9ImZpbHRlcjFfZiIgeD0iLTU3MC45OTkiIHk9IjUzMy4wODkiIHdpZHRoPSIxNTc0LjQ5IiBoZWlnaHQ9IjE1NzQuNDkiIGZpbHRlclVuaXRzPSJ1c2VyU3BhY2VPblVzZSIgY29sb3ItaW50ZXJwb2xhdGlvbi1maWx0ZXJzPSJzUkdCIj4KPGZlRmxvb2QgZmxvb2Qtb3BhY2l0eT0iMCIgcmVzdWx0PSJCYWNrZ3JvdW5kSW1hZ2VGaXgiLz4KPGZlQmxlbmQgbW9kZT0ibm9ybWFsIiBpbj0iU291cmNlR3JhcGhpYyIgaW4yPSJCYWNrZ3JvdW5kSW1hZ2VGaXgiIHJlc3VsdD0ic2hhcGUiLz4KPGZlR2F1c3NpYW5CbHVyIHN0ZERldmlhdGlvbj0iMjUwIiByZXN1bHQ9ImVmZmVjdDFfZm9yZWdyb3VuZEJsdXIiLz4KPC9maWx0ZXI+CjxmaWx0ZXIgaWQ9ImZpbHRlcjJfZiIgeD0iMzAxIiB5PSItMjE1IiB3aWR0aD0iMjcwOS4zMyIgaGVpZ2h0PSIyNjUwLjg5IiBmaWx0ZXJVbml0cz0idXNlclNwYWNlT25Vc2UiIGNvbG9yLWludGVycG9sYXRpb24tZmlsdGVycz0ic1JHQiI+CjxmZUZsb29kIGZsb29kLW9wYWNpdHk9IjAiIHJlc3VsdD0iQmFja2dyb3VuZEltYWdlRml4Ii8+CjxmZUJsZW5kIG1vZGU9Im5vcm1hbCIgaW49IlNvdXJjZUdyYXBoaWMiIGluMj0iQmFja2dyb3VuZEltYWdlRml4IiByZXN1bHQ9InNoYXBlIi8+CjxmZUdhdXNzaWFuQmx1ciBzdGREZXZpYXRpb249IjI1MCIgcmVzdWx0PSJlZmZlY3QxX2ZvcmVncm91bmRCbHVyIi8+CjwvZmlsdGVyPgo8bGluZWFyR3JhZGllbnQgaWQ9InBhaW50MF9saW5lYXIiIHgxPSIxMDAwIiB5MT0iMC4wODc3Njg2IiB4Mj0iMTAwMCIgeTI9IjIyMTkuMDkiIGdyYWRpZW50VW5pdHM9InVzZXJTcGFjZU9uVXNlIj4KPHN0b3Agc3RvcC1jb2xvcj0iIzRBN0RGRiIvPgo8c3RvcCBvZmZzZXQ9IjAuNDYzNTQyIiBzdG9wLWNvbG9yPSIjRkY4QURFIi8+CjxzdG9wIG9mZnNldD0iMC44ODAyMDgiIHN0b3AtY29sb3I9IiNGRkM5RjAiIHN0b3Atb3BhY2l0eT0iMCIvPgo8c3RvcCBvZmZzZXQ9IjEiIHN0b3AtY29sb3I9IiNGRkM5RjAiIHN0b3Atb3BhY2l0eT0iMCIvPgo8L2xpbmVhckdyYWRpZW50Pgo8bGluZWFyR3JhZGllbnQgaWQ9InBhaW50MV9saW5lYXIiIHgxPSIzMjMuNTY0IiB5MT0iLTI0OS4zOCIgeDI9IjMyMy41NjQiIHkyPSI4MTguNTA3IiBncmFkaWVudFVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+CjxzdG9wIHN0b3AtY29sb3I9IiMwMEZGRjAiLz4KPHN0b3Agb2Zmc2V0PSIxIiBzdG9wLWNvbG9yPSIjMkMwMEFBIi8+CjwvbGluZWFyR3JhZGllbnQ+CjxsaW5lYXJHcmFkaWVudCBpZD0icGFpbnQyX2xpbmVhciIgeDE9IjE2NTUuNjciIHkxPSIzNzkuNDQ2IiB4Mj0iMTY1NS42NyIgeTI9IjE4NDEuNDUiIGdyYWRpZW50VW5pdHM9InVzZXJTcGFjZU9uVXNlIj4KPHN0b3Agc3RvcC1jb2xvcj0iI0ZGOTkwMCIvPgo8c3RvcCBvZmZzZXQ9IjEiIHN0b3AtY29sb3I9IiNGRkU3MEMiLz4KPC9saW5lYXJHcmFkaWVudD4KPGNsaXBQYXRoIGlkPSJjbGlwMCI+CjxyZWN0IHdpZHRoPSIyMDAwIiBoZWlnaHQ9IjIyMTkiIGZpbGw9IndoaXRlIi8+CjwvY2xpcFBhdGg+CjwvZGVmcz4KPC9zdmc+Cg==")',
          backgroundPosition: 'center'
        }}
      >
        <HeaderInner>
          <FadeInUp delay={500}>
            <HeaderLogo logo={<Logo size={34} />}>Weekrise</HeaderLogo>
          </FadeInUp>

          <Spacer height="max(5vh, 16px)" />

          <FadeIn>
            <MainHeader>
              A beautiful weekly <br /> task calendar.
            </MainHeader>
          </FadeIn>

          <Spacer height="max(3vh, 16px)" />

          <FadeIn delay={300}>
            <BigSubtitle>
              Weekrise makes it easy and intuitive <br className="hr-desktop" />
              to organize all aspects of life.
            </BigSubtitle>
          </FadeIn>

          <Spacer height="max(3vh, 16px)" />

          <FadeIn delay={600}>
            <Buttons>
              <Link href={loggedIn ? 'https://app.weekrise.com' : '/auth/signup'}>
                <a>
                  <Button
                    size="big"
                    variant={{
                      normal: {
                        background: 'var(--vapor-primary)',
                        border: 'var(--vapor-primary)',
                        color: 'var(--vapor-primary-text)'
                      },
                      focus: {
                        background: 'var(--vapor-pink)',
                        border: 'var(--vapor-pink)',
                        color: 'var(--vapor-primary-text)'
                      }
                    }}
                  >
                    Get Started
                  </Button>
                </a>
              </Link>

              <Button
                size="big"
                variant="secondary"
                onClick={() => {
                  if (!featuresStartRef.current) return;

                  window.scrollTo({
                    top: featuresStartRef.current.getBoundingClientRect().top - window.scrollY + 70,
                    behavior: 'smooth'
                  });
                }}
              >
                Learn more
              </Button>
            </Buttons>
          </FadeIn>
        </HeaderInner>

        <Center>
          <FadeInUp delay={1600}>
            <Demo size={300} />
          </FadeInUp>
        </Center>

        <Spacer height="2em" />

        <Center style={{ zIndex: 5, position: 'relative' }}>
          <FadeIn delay={1000}>
            <DemoFrame src={demoUrl} canLoad={!loading} initialImage="/screenshot.webp" title="A demo of Weekrise" />
          </FadeIn>
        </Center>

        <HeaderMask />
      </Header>

      <div className="features-waypoint" ref={featuresStartRef} />

      <BigContent>
        <FadeIn whenInView>
          <BigTitle style={{ color: '#00ABDE' }}>
            Task management, designed for <br className="hr-desktop" /> people, not robots.
          </BigTitle>
        </FadeIn>

        <Spacer height="2.2em" />

        <FadeIn delay={200} whenInView>
          <Subtitle>Weekrise is built to help you, not to get in your way.</Subtitle>
        </FadeIn>

        <Spacer height="1.6em" />

        <FadeIn delay={400} whenInView>
          <Subtitle style={{ color: '#888' }}>Weekrise’s feature-set has been carefully chosen to help you get your work done without getting in your way.</Subtitle>
        </FadeIn>

        <Spacer height="1.6em" />

        <FadeIn delay={500} whenInView>
          <Subtitle style={{ color: '#888' }}>Tasks intentionally don’t have times associated with them that way you’re not bound to strict timeframes. And if something takes a bit longer than anticipated, no rescheduling is required.</Subtitle>
        </FadeIn>
      </BigContent>

      <FadeIn whenInView>
        <BigCard
          image={<img src="/assets/features/projects.webp" />}
          background="linear-gradient(270deg, #8EC5FC 0%, #E0C3FC 100%);"
          color="white"
          title={
            <>
              Get organized <br className="hr-desktop" />
              with projects.
            </>
          }
          description={<>With projects you can keep track of goals or just group tasks together.</>}
        >
          In Weekrise, projects allow you to organize tasks further. Not only can you assign projects to tasks on the calendar, but you also have a board for each project where you can put tasks for which you don’t have a date.
        </BigCard>
      </FadeIn>

      <FadeIn whenInView>
        <ReallyBigText>
          Enhance your productivity without
          <br className="hr-desktop" /> spending hours organizing <br className="hr-desktop" />
          calendars and todo-lists.
        </ReallyBigText>
      </FadeIn>

      <HighlightSection
        style={{
          background: 'linear-gradient(121.39deg, #FFC267 0.38%, #FFB1D7 100%)'
        }}
      >
        <HighlightSectionInner>
          <Grid>
            <VerticalCenter>
              <FadeIn whenInView>
                <BigTitle>
                  Work together, <br className="hr-desktop" />
                  seamlessly.
                </BigTitle>
              </FadeIn>

              <Spacer height="1.7em" />

              <FadeIn whenInView delay={150}>
                <Subtitle>Weekrise is built with collaboration and sharing in mind.</Subtitle>
              </FadeIn>

              <Spacer height="0.9em" />

              <FadeIn whenInView delay={300}>
                <Subtitle style={{ opacity: 0.8 }}>Invite family, friends, co-workers, or anyone else and collaborate using Weekrise.</Subtitle>
              </FadeIn>
            </VerticalCenter>

            <FadeIn whenInView delay={200}>
              <Center style={{ textAlign: 'center' }}>
                <img src="/assets/features/people.webp" alt="An image of people" style={{ width: '60%' }} />
              </Center>
            </FadeIn>
          </Grid>

          <Spacer height="max(7vh, 20px)" />

          <CardGrid style={{ maxWidth: '100%', margin: '0px' }}>
            <FadeIn whenInView>
              <Card
                background="rgba(255, 255, 255)"
                icon={<TeamIcon size={50} />}
                title={
                  <>
                    Share it with <br className="hr-desktop" />
                    <Mark color="#FFC267">your team</Mark>.
                  </>
                }
              >
                Invite as many people as you want to your calendar. <br />
                You can easily assign tasks and see who's working on what.
              </Card>
            </FadeIn>

            <FadeIn whenInView>
              <Card
                background="rgba(255, 255, 255)"
                icon={<ShareIcon size={50} />}
                title={
                  <>
                    Share it with <br className="hr-desktop" />
                    <Mark color="#FFB1D7">the world</Mark>.
                  </>
                }
              >
                Make your calendar public for everyone to see - they won't even need a Weekrise account.
              </Card>
            </FadeIn>
          </CardGrid>
        </HighlightSectionInner>
      </HighlightSection>

      <CardGridWrapper>
        <div style={{ marginBottom: 'max(20px, 10vh)', textAlign: 'center' }}>
          <BigTitle>We've got you covered!</BigTitle>

          <Spacer height="1.6em" />

          <Subtitle>A feature-set that makes sense.</Subtitle>
        </div>

        <CardGrid>
          <FadeIn whenInView>
            <Card
              icon={<MobileIcon size={50} />}
              title={
                <>
                  Manage tasks <br className="hr-desktop" />
                  <Mark color="#FFBB00">on the go</Mark>.
                </>
              }
            >
              Weekrise is optimized for mobile devices.
              <Spacer height="0.6em" />
              No matter if you're in the desert, on a plane, or at the office. Weekrise is always with you.
              <Spacer height="0.6em" />
              The best part is: our mobile experience has the fully-fledged feature set of Weekrise.
            </Card>
          </FadeIn>

          <FadeIn whenInView>
            <Card
              icon={<RepeatIcon size={50} />}
              title={
                <>
                  <Mark color="#45AAF2">Repeating</Mark> tasks.
                </>
              }
            >
              Things repeat! That's why Weekrise has full support for repeating tasks.
              <Spacer height="0.6em" />
              Our repeats are smarter than simply copying a task. Repeating tasks synchronize certain things, like the project, but keep other things independent, like subtasks and the done status.
            </Card>
          </FadeIn>

          <FadeIn whenInView>
            <Card
              icon={<SubtaskIcon size={50} />}
              title={
                <>
                  Get detailed, <br className="hr-desktop" /> with <Mark color="#10ac84">subtasks</Mark>.
                </>
              }
            >
              With Weekrise you can split up large tasks into smaller subtasks.
              <Spacer height="0.6em" />
              Subtasks are a great way to keep track of your progress.
            </Card>
          </FadeIn>

          <FadeIn whenInView>
            <Card
              icon={<SyncIcon size={50} />}
              title={
                <>
                  Always in <Mark color="#FC5C65">sync</Mark>.
                </>
              }
            >
              Your Tasks, Projects, and Calendars are synchronized across all your devices.
              <Spacer height="0.6em" />
              You can create events on your computer and pick up where you left off on your phone.
            </Card>
          </FadeIn>

          <FadeIn whenInView>
            <Card
              icon={<TodayIcon size={50} />}
              title={
                <>
                  <Mark color="#A55EEA">Today</Mark>'s tasks.
                </>
              }
            >
              With the Today View, you can see all tasks that are due today across all calendars.
              <Spacer height="0.6em" />
              No more switching between calendars.
            </Card>
          </FadeIn>

          <FadeIn whenInView>
            <Card
              icon={<MultiCalendarIcon size={50} />}
              title={
                <>
                  <Mark color="#3867D6">Multi-Calendar</Mark> support.
                </>
              }
            >
              Not everything fits into a single calendar.
              <Spacer height="0.6em" />
              That's why Weekrise supports multiple calendars. For example one for work, one for personal things, and one for that side-project you're working on.
            </Card>
          </FadeIn>
        </CardGrid>
      </CardGridWrapper>

      <BottomCTA
        title={
          <>
            Get Organized
            <br />
            with Weekrise.
          </>
        }
        action="Get started"
        actionLink={loggedIn ? 'https://app.weekrise.com' : '/auth/signup'}
        image={{
          src: '/assets/bottom-cta.png',
          label: 'A demo image of Weekrise'
        }}
      />
    </MarketingLayout>
  );
}
